import { observer, useSelector } from '@legendapp/state/react';
import { createContext, type FC, type PropsWithChildren, useContext, useCallback, useMemo } from 'react';
import { signIn, signOut } from 'next-auth/react';
import { useIdentityRequestOtp } from '../hooks/identity/use-identity-request-otp';
import { useIdentityVerifyOtp } from '../hooks/identity/use-identity-verify-otp';
import { useRequestValidatePhone as useLegacyRequestValidatePhone } from '../hooks/use-request-validate-phone';
import { useConfirmRequestedValidationPhone as useLegacyConfirmOtp } from '../hooks/use-confirm-requested-validation-phone';
import type { AuthContextProps, InitValidatePhoneParams, RequestValidatePhoneParams, ResultData } from '../types';
import { type RegisterAccountInfo, ValidatePhoneType } from '../types/front.graphql';
import { initialAuthContext } from './initial-auth.provider';
import { authStore$, clearChannelAndOtpData, clearOtpSessionData } from '../stores/auth.store';
import { identityAuthStore$, clearIdentityAuthData } from '../stores/identity-auth.store';
import { resetIdentityClient } from '../api/identity/identity-client';
import { ErrorCodesEnum } from '../enums';
import { useRegisterNewAccountByOtp } from '../hooks';
import { resetOtpAttemptData } from '../utils/otp-attempt-tracker';

const AuthContext = createContext<AuthContextProps>(initialAuthContext);

export const useAuthProvider = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error(`useAuth must be used within an AuthProvider`);
    }
    return useMemo(() => context, [context]);
};

/**
 * AuthProvider — Unified Login/Register flow.
 *
 * Single flow (no login/register toggle):
 * Phone → Channel → Try Cognito OTP first:
 *   - User exists → Verify (Cognito) → signIn → done
 *   - User NOT found → auto-fallback to Magento Register OTP → Verify (Magento)
 *     → Register form → create account → signIn → done
 *
 * The system auto-detects whether the user exists and routes accordingly.
 * No manual login/register mode switching required.
 */
export const AuthProvider: FC<PropsWithChildren> = observer(({ children }) => {
    // Identity hooks (Cognito, federation gateway)
    const requestIdentityOtp = useIdentityRequestOtp();
    const verifyIdentityOtp = useIdentityVerifyOtp();

    // Legacy hooks (Magento, for registration phone verification when user doesn't exist in Cognito)
    const legacyRequestValidatePhone = useLegacyRequestValidatePhone();
    const legacyConfirmOtp = useLegacyConfirmOtp();
    const legacyRegisterAccount = useRegisterNewAccountByOtp();

    const isAuthenticated = useSelector(() => identityAuthStore$.isAuthenticated.get() ?? false);

    /**
     * initValidatePhone — Stores phone number and referral code for later use.
     *
     * When the phone number changes from a previous attempt, all OTP session
     * and detection state is cleared to force Cognito re-detection for the
     * new number. This prevents stale validatePhoneType (e.g. Register from
     * a prior abandoned flow) from short-circuiting requestValidatePhone.
     */
    const initValidatePhone = useCallback(async (params: InitValidatePhoneParams) => {
        const previousPhone = authStore$.initValidatePhoneData.get()?.phoneNumber;

        if (previousPhone && previousPhone !== params.phoneNumber) {
            // Phone number changed — reset detection and OTP state
            clearChannelAndOtpData();
            clearIdentityAuthData();
            authStore$.validatePhoneType.set(undefined);
            authStore$.authMode.set('login');
        }

        authStore$.initValidatePhoneData.set(params);
        identityAuthStore$.phoneNumber.set(params.phoneNumber);
    }, []);

    /**
     * requestValidatePhone — Unified flow: always try Cognito first, auto-fallback on USER_NOT_FOUND.
     *
     * Flow:
     * 1. If validatePhoneType is already set (resend scenario), use the corresponding flow directly.
     * 2. Otherwise, always try Cognito login OTP first.
     * 3. If Cognito returns USER_NOT_FOUND → auto-switch to Register mode
     *    and send Magento OTP immediately (no user interaction needed).
     * 4. Other Cognito errors → show error to user.
     */
    const requestValidatePhone = useCallback(
        async (params: RequestValidatePhoneParams): Promise<ResultData> => {
            const phoneNumber = authStore$.initValidatePhoneData.get()?.phoneNumber ?? '';
            if (!phoneNumber) {
                return { message: 'Phone number is required', errorCode: ErrorCodesEnum.INVALID_REQUEST };
            }

            const channel = params.otpChannel === 'ZALO' ? 'ZALO' : 'SMS';

            // ─── Clear stale OTP session data before every new request ───────
            // This ensures no leftover confirmResponse, currentOtp, or attempt
            // counters from a previous session leak into the new one —
            // regardless of login, register, or resend scenario.
            clearOtpSessionData();
            resetOtpAttemptData();
            identityAuthStore$.error.set(undefined);
            identityAuthStore$.challengeSession.set(undefined);

            // If validatePhoneType is already set (e.g. resend OTP, or post-registration login),
            // use the corresponding flow directly to avoid re-detecting user existence.
            const currentType = authStore$.validatePhoneType.get();

            if (currentType === ValidatePhoneType.Register) {
                // ─── Resend Register OTP: Magento ────────────────────────────
                return await legacyRequestValidatePhone(params, ValidatePhoneType.Register);
            }

            // ─── Try Cognito Login first ─────────────────────────────────────
            authStore$.validatePhoneType.set(ValidatePhoneType.Login);
            const result = await requestIdentityOtp({ phoneNumber, notificationChannel: channel });

            if (result.success) {
                authStore$.otpStartTime.set(Math.floor(Date.now() / 1000));
                return { message: 'OTP sent successfully', errorCode: ErrorCodesEnum.SUCCESS };
            }

            // ─── User not found → auto-switch to Register + send Magento OTP ─
            // Auth Service returns errorCode: 'UserNotFoundException' from Cognito
            // when adminGetUser fails (user doesn't exist in the pool).
            if (result.errorCode === 'UserNotFoundException') {
                authStore$.validatePhoneType.set(ValidatePhoneType.Register);
                authStore$.authMode.set('register');
                return await legacyRequestValidatePhone(params, ValidatePhoneType.Register);
            }

            // ─── Other Cognito errors → show to user ─────────────────────────
            return {
                message: result.message || 'Failed to send OTP',
                errorCode: result.errorCode || ErrorCodesEnum.INVALID_REQUEST,
            };
        },
        [requestIdentityOtp, legacyRequestValidatePhone]
    );

    /**
     * confirmRequestedValidationPhone — Verifies OTP.
     *
     * Checks validatePhoneType to decide which verification path:
     * - Register type: Legacy Magento verify → OTP is for phone ownership before account creation.
     *   OtpVerification component will then navigate to Register form.
     * - Login type: Cognito verifyOtpChallenge → OTP is for authentication, signIn happens inside.
     */
    const confirmRequestedValidationPhone = useCallback(
        async (otp: string): Promise<ResultData> => {
            const validateType = authStore$.validatePhoneType.get();

            if (validateType === ValidatePhoneType.Register) {
                // Registration phone verification: use legacy Magento OTP verify.
                // legacyConfirmOtp stores the confirm response + otp in authStore$.
                // The OtpVerification component checks validatePhoneType and navigates to Register form.
                try {
                    const result = await legacyConfirmOtp(otp);
                    return result;
                } catch (error) {
                    const message = error instanceof Error ? error.message : 'OTP verification failed';
                    return { message, errorCode: ErrorCodesEnum.INVALID_REQUEST };
                }
            }

            // Login authentication: use Cognito OTP verify + signIn
            const result = await verifyIdentityOtp({ otp });

            if (result.success) {
                authStore$.isAuthenticated.set(true);
                return { message: 'OTP verified successfully', errorCode: ErrorCodesEnum.SUCCESS };
            }

            return {
                message: result.message || 'OTP verification failed',
                errorCode: result.errorCode || ErrorCodesEnum.INVALID_REQUEST,
            };
        },
        [verifyIdentityOtp, legacyConfirmOtp]
    );

    /**
     * loginWithPhoneAndOtp — No-op because verify already creates the session.
     *
     * For Login type: verifyIdentityOtpChallenge already calls signIn('identity-otp') internally.
     * This returns SUCCESS so the OtpVerification component continues its flow.
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const loginWithPhoneAndOtp = useCallback(async (_otp: string): Promise<ResultData> => {
        return { message: 'Login successful', errorCode: ErrorCodesEnum.SUCCESS };
    }, []);

    /**
     * registerNewAccountByOtp — Creates account and authenticates in one step.
     *
     * Called AFTER registration OTP is verified (phone ownership confirmed).
     * Flow:
     * 1. Magento registerNewAccountByOtp → creates customer → returns customer_token
     * 2. signIn('identity-register') with commerce tokens → creates NextAuth session
     * 3. Done — user is authenticated!
     *
     * Fallback (if no token returned or signIn fails):
     * 1. Switch to Login flow → user can do Cognito OTP to authenticate
     */
    const registerNewAccountByOtp = useCallback(
        async (accountInfo: RegisterAccountInfo): Promise<ResultData> => {
            const phoneNumber = authStore$.initValidatePhoneData.get()?.phoneNumber ?? '';
            const defaultEmail = `${phoneNumber.replace(/[^0-9]/g, '')}.customer@kidsplaza.vn`;

            const result = await legacyRegisterAccount({
                email: accountInfo.email?.trim() || defaultEmail,
                first_name: accountInfo?.first_name?.trim() || 'Ba',
                last_name: accountInfo?.last_name?.trim() || 'Mẹ',
                password: accountInfo?.password,
                website_id: accountInfo?.website_id,
            });

            if (result.errorCode !== ErrorCodesEnum.SUCCESS) {
                return result;
            }

            // Registration succeeded — attempt to create session with returned token
            const customerToken = result.customerToken;
            if (!customerToken?.access_token) {
                // No token returned — fallback to login flow
                authStore$.validatePhoneType.set(ValidatePhoneType.Login);
                authStore$.authMode.set('login');
                return {
                    message: 'Registration successful. Please verify to login.',
                    errorCode: ErrorCodesEnum.OTP_VERIFICATION_REQUIRED,
                };
            }

            try {
                const signInResult = await signIn('identity-register', {
                    redirect: false,
                    commerceAccessToken: customerToken.access_token,
                    commerceRefreshToken: customerToken.refresh_token ?? '',
                    commerceExpiresIn: String(customerToken.expires_in ?? 3600),
                    phone: phoneNumber,
                    userId: '',
                });

                if (signInResult?.error) {
                    // signIn failed — fallback to login flow
                    authStore$.validatePhoneType.set(ValidatePhoneType.Login);
                    authStore$.authMode.set('login');
                    return {
                        message: 'Registration successful. Please verify to login.',
                        errorCode: ErrorCodesEnum.OTP_VERIFICATION_REQUIRED,
                    };
                }

                authStore$.isAuthenticated.set(true);
                return { message: 'Register successful', errorCode: ErrorCodesEnum.SUCCESS };
            } catch {
                // Network error on signIn — fallback to login flow
                authStore$.validatePhoneType.set(ValidatePhoneType.Login);
                authStore$.authMode.set('login');
                return {
                    message: 'Registration successful. Please verify to login.',
                    errorCode: ErrorCodesEnum.OTP_VERIFICATION_REQUIRED,
                };
            }
        },
        [legacyRegisterAccount]
    );

    const logout = useCallback(async () => {
        clearIdentityAuthData();
        resetIdentityClient();
        authStore$.isAuthenticated.set(false);
        await signOut({ redirect: true, callbackUrl: '/' });
    }, []);

    const value = useMemo(
        () => ({
            isAuthenticated,
            logout,
            isLoggingOut: false,
            isAuthenticating: false,
            initValidatePhone,
            requestValidatePhone,
            loginWithPhoneAndOtp,
            registerNewAccountByOtp,
            confirmRequestedValidationPhone,
        }),
        [
            isAuthenticated,
            logout,
            initValidatePhone,
            requestValidatePhone,
            loginWithPhoneAndOtp,
            registerNewAccountByOtp,
            confirmRequestedValidationPhone,
        ]
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
});
